<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { Button, Dialog, Message, Select } from 'primevue';
import InputNumber from 'primevue/inputnumber';
import InputGroup from 'primevue/inputgroup';
import InputGroupAddon from 'primevue/inputgroupaddon';
import Textarea from 'primevue/textarea';
import { useOutboundDeliveryStore } from '../stores/outboundDeliveryStore';
import { useRefreshOutboundDeliveryStore } from '../stores/refreshOutboundDeliveryStore';
import { useAddOutboundDeliveryTransactionApi } from '../api/addOutboundDeliveryTransactionApi';
import { useProductStore } from 'shared/products/stores/productStore';
import { useStorageAreaStore } from 'shared/storageAreas/stores/storageAreaStore';
import { useLanguageStore } from 'shared/translations/stores/languageStore';
import { useActionFailedAlert } from 'shared/problems/composables/actionFailedAlert';
import { storageAreaMatchesProductType } from '../domain/storageAreaMatchesProductType';
import { convertToTon, Unit } from 'shared/common/domain/bulkUnits';
import { DeliveryStatus } from 'shared/deliveries/domain/deliveryStatus';

interface SelectOption {
  label: string;
  value: string;
}

type DeliveryFilterMode = 'matchingStorageArea' | 'all';

interface ShowOptions {
  storageAreaId?: string;
  filterMode?: DeliveryFilterMode;
}

defineExpose({
  show,
});

const languageStore = useLanguageStore();
const { translations } = storeToRefs(languageStore);

const outboundDeliveryStore = useOutboundDeliveryStore();
const refreshOutboundDeliveryStore = useRefreshOutboundDeliveryStore();
const productStore = useProductStore();
const storageAreaStore = useStorageAreaStore();
const addTransactionApi = useAddOutboundDeliveryTransactionApi();
const actionFailedAlert = useActionFailedAlert();

const visible = ref(false);
const loading = ref(false);

const selectedDeliveryId = ref<string | null>(null);
const selectedDeliveryRowId = ref<string | null>(null);
const selectedStorageAreaId = ref<string | null>(null);
const weight = ref<number | null>(null);
const selectedContextStorageAreaId = ref<string | null>(null);
const deliveryFilterMode = ref<DeliveryFilterMode>('all');
const note = ref<string | null>(null);

const isContextOpen = computed(() => selectedContextStorageAreaId.value !== null);

const hasMatchingDeliveriesForContext = computed(() => {
  const storageAreaId = selectedContextStorageAreaId.value;
  if (!storageAreaId) {
    return false;
  }

  return outboundDeliveryStore.list.some((delivery) =>
    delivery.rows.some((row) => row.storageAreaId === storageAreaId),
  );
});

const effectiveFilterMode = computed<DeliveryFilterMode>(() => {
  if (!isContextOpen.value) {
    return 'all';
  }

  return deliveryFilterMode.value;
});

const filterModeOptions = computed<SelectOption[]>(() => {
  const matchingOption: SelectOption = {
    label: translations.value.outboundDelivery.transactionDialog.filterMatchingStorageArea,
    value: 'matchingStorageArea',
  };

  return [
    matchingOption,
    {
      label: translations.value.outboundDelivery.transactionDialog.filterAllDeliveries,
      value: 'all',
    },
  ];
});

const deliveryOptions = computed<SelectOption[]>(() => {
  let filteredDeliveries = effectiveFilterMode.value === 'matchingStorageArea' && selectedContextStorageAreaId.value
    ? outboundDeliveryStore.list.filter((delivery) =>
      delivery.rows.some((row) => row.storageAreaId === selectedContextStorageAreaId.value),
    )
    : outboundDeliveryStore.list;

  filteredDeliveries = filteredDeliveries.filter((delivery) => [DeliveryStatus.Released, DeliveryStatus.Active].includes(delivery.status));

  return [...filteredDeliveries]
    .sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }))
    .map((delivery) => ({
      label: delivery.name,
      value: delivery.id,
    }));
});

const selectedDelivery = computed(() => {
  if (!selectedDeliveryId.value) return null;
  return outboundDeliveryStore.getById(selectedDeliveryId.value) ?? null;
});

const deliveryRowOptions = computed<SelectOption[]>(() => {
  const delivery = selectedDelivery.value;
  if (!delivery) return [];

  const filteredRows = effectiveFilterMode.value === 'matchingStorageArea' && selectedContextStorageAreaId.value
    ? delivery.rows.filter((row) => row.storageAreaId === selectedContextStorageAreaId.value)
    : delivery.rows;

  return filteredRows.map((row, index) => {
    const productName = productStore.getById(row.productId)?.name ?? row.productId;
    return {
      label: `${index + 1}. ${productName}`,
      value: row.id,
    };
  });
});

const selectedDeliveryRow = computed(() => {
  const delivery = selectedDelivery.value;
  if (!delivery || !selectedDeliveryRowId.value) return null;
  return delivery.rows.find((row) => row.id === selectedDeliveryRowId.value) ?? null;
});

const hasSelectionDetails = computed(() => Boolean(
  selectedDelivery.value?.customer?.trim() || selectedDelivery.value?.note?.trim() || selectedDeliveryRow.value?.note?.trim(),
));

const selectedProductUnit = computed(() => {
  const row = selectedDeliveryRow.value;
  if (!row) return Unit.MetricTon;

  return productStore.getById(row.productId)?.bulkData?.unit ?? Unit.MetricTon;
});

const storageAreaOptions = computed<SelectOption[]>(() => {
  const contextStorageAreaId = selectedContextStorageAreaId.value;

  const row = selectedDeliveryRow.value;
  if (!row) {
    if (!contextStorageAreaId) return [];

    const contextStorageArea = storageAreaStore.getById(contextStorageAreaId);
    return contextStorageArea
      ? [{ label: contextStorageArea.name, value: contextStorageArea.id }]
      : [];
  }

  const product = productStore.getById(row.productId);
  if (!product) {
    if (!contextStorageAreaId) return [];

    const contextStorageArea = storageAreaStore.getById(contextStorageAreaId);
    return contextStorageArea
      ? [{ label: contextStorageArea.name, value: contextStorageArea.id }]
      : [];
  }

  const options = storageAreaStore.storageAreas
    .filter((area) => storageAreaMatchesProductType(product.type, area.type))
    .sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }))
    .map((area) => ({
      label: area.name,
      value: area.id,
    }));

  if (!contextStorageAreaId) {
    return options;
  }

  const hasContextStorageArea = options.some((option) => option.value === contextStorageAreaId);
  if (hasContextStorageArea) {
    return options;
  }

  const contextStorageArea = storageAreaStore.getById(contextStorageAreaId);
  if (!contextStorageArea) {
    return options;
  }

  return [
    {
      label: contextStorageArea.name,
      value: contextStorageArea.id,
    },
    ...options,
  ];
});

const formError = computed(() => {
  if (!selectedDeliveryId.value) {
    return translations.value.outboundDelivery.transactionDialog.deliveryRequired;
  }

  if (!selectedDeliveryRowId.value) {
    return translations.value.outboundDelivery.transactionDialog.deliveryRowRequired;
  }

  if (!selectedStorageAreaId.value) {
    return translations.value.outboundDelivery.transactionDialog.storageAreaRequired;
  }

  if (weight.value === null) {
    return translations.value.outboundDelivery.transactionDialog.weightRequired;
  }

  return undefined;
});

const saveDisabled = computed(() => loading.value || formError.value !== undefined);

watch(selectedDeliveryId, () => {
  selectedDeliveryRowId.value = deliveryRowOptions.value.length === 1 ? deliveryRowOptions.value[0]!.value : null;
  selectedStorageAreaId.value = isContextOpen.value ? selectedContextStorageAreaId.value : null;
});

watch(selectedDeliveryRowId, () => {
  if (isContextOpen.value) {
    selectedStorageAreaId.value = selectedContextStorageAreaId.value;
    return;
  }

  const row = selectedDeliveryRow.value;
  if (!row) {
    selectedStorageAreaId.value = isContextOpen.value ? selectedContextStorageAreaId.value : null;
    return;
  }

  const allowed = storageAreaOptions.value.some((option) => option.value === row.storageAreaId);
  selectedStorageAreaId.value = allowed ? row.storageAreaId : null;
});

watch(effectiveFilterMode, () => {
  const validDeliveryIds = new Set(deliveryOptions.value.map((option) => option.value));
  if (!selectedDeliveryId.value || !validDeliveryIds.has(selectedDeliveryId.value)) {
    selectedDeliveryId.value = deliveryOptions.value.length === 1 ? deliveryOptions.value[0]!.value : null;
    selectedDeliveryRowId.value = selectedDeliveryId.value && deliveryRowOptions.value.length === 1
      ? deliveryRowOptions.value[0]!.value
      : null;
    return;
  }

  const validRowIds = new Set(deliveryRowOptions.value.map((option) => option.value));
  if (!selectedDeliveryRowId.value || !validRowIds.has(selectedDeliveryRowId.value)) {
    selectedDeliveryRowId.value = deliveryRowOptions.value.length === 1 ? deliveryRowOptions.value[0]!.value : null;
  }
});

function reset() {
  selectedDeliveryId.value = null;
  selectedDeliveryRowId.value = null;
  selectedStorageAreaId.value = null;
  selectedContextStorageAreaId.value = null;
  deliveryFilterMode.value = 'all';
  weight.value = null;
  note.value = null;
  loading.value = false;
}

async function show(options?: ShowOptions) {
  reset();
  selectedContextStorageAreaId.value = options?.storageAreaId ?? null;
  await refreshOutboundDeliveryStore.initialize();

  if (isContextOpen.value) {
    selectedStorageAreaId.value = selectedContextStorageAreaId.value;
    deliveryFilterMode.value = hasMatchingDeliveriesForContext.value
      ? 'matchingStorageArea'
      : 'all';
  } else {
    deliveryFilterMode.value = options?.filterMode ?? 'all';
  }

  visible.value = true;
}

function hide() {
  visible.value = false;
  reset();
}

function weightToTon(value: number, productId: string): number {
  const bulkData = productStore.getById(productId)?.bulkData;
  if (!bulkData) {
    return value;
  }

  if (bulkData.unit === Unit.CubicMeter && bulkData.density <= 0) {
    return value;
  }

  return convertToTon(value, bulkData.unit, bulkData.density);
}

async function save() {
  const row = selectedDeliveryRow.value;

  if (!row || !selectedDeliveryId.value || !selectedStorageAreaId.value || weight.value === null) {
    return;
  }

  loading.value = true;
  try {
    const response = await addTransactionApi.execute({
      entity: {
        deliveryId: selectedDeliveryId.value,
        deliveryRowId: row.id,
        productId: row.productId,
        storageAreaId: selectedStorageAreaId.value,
        weight: weightToTon(weight.value, row.productId),
        volume: null,
        comment: note.value,
      },
    });

    if (!response.ok) {
      actionFailedAlert.show(response.details);
      return;
    }

    hide();
  } finally {
    loading.value = false;
  }
}
</script>

<template>
  <Dialog v-model:visible="visible" modal :draggable="false"
    :header="translations.outboundDelivery.transactionDialog.title"
    :style="{ width: hasSelectionDetails ? '54rem' : '34rem', maxWidth: '95vw' }">
    <div class="dialog-layout" :class="{ 'has-details': hasSelectionDetails }">
      <form class="form-grid" @submit.prevent="save">
        <div v-if="isContextOpen" class="field">
          <label for="outbound-transaction-filter-mode">{{ translations.outboundDelivery.transactionDialog.filterMode
            }}</label>
          <Select id="outbound-transaction-filter-mode" v-model="deliveryFilterMode" :options="filterModeOptions"
            optionLabel="label" optionValue="value"></Select>
        </div>

        <div class="field">
          <label for="outbound-transaction-delivery-select">{{ translations.outboundDelivery.transactionDialog.delivery
            }}</label>
          <Select id="outbound-transaction-delivery-select" v-model="selectedDeliveryId" :options="deliveryOptions"
            optionLabel="label" optionValue="value" filter showClear
            :placeholder="translations.outboundDelivery.transactionDialog.selectDelivery"></Select>
        </div>

        <div class="field">
          <label for="outbound-transaction-delivery-row-select">{{
            translations.outboundDelivery.transactionDialog.deliveryRow }}</label>
          <Select id="outbound-transaction-delivery-row-select" v-model="selectedDeliveryRowId"
            :options="deliveryRowOptions" optionLabel="label" optionValue="value" filter showClear
            :disabled="!selectedDeliveryId"
            :placeholder="translations.outboundDelivery.transactionDialog.selectDeliveryRow"></Select>
        </div>

        <div class="field">
          <label for="outbound-transaction-storage-area-select">{{ translations.generic.storageArea }}</label>
          <Select id="outbound-transaction-storage-area-select" v-model="selectedStorageAreaId"
            :options="storageAreaOptions" optionLabel="label" optionValue="value" filter :showClear="!isContextOpen"
            :disabled="isContextOpen || !selectedDeliveryRowId"
            :placeholder="translations.outboundDelivery.transactionDialog.selectStorageArea"></Select>
        </div>

        <div class="field">
          <label for="outbound-transaction-weight-input">{{ translations.outboundDelivery.editPage.rowWeight }}</label>
          <InputGroup>
            <InputNumber pt:pcInputText:root:autofocus v-model:model-value="weight" :max-fraction-digits="3"
              input-id="outbound-transaction-weight-input" class="number-field"></InputNumber>
            <InputGroupAddon>{{ translations.bulk.bulkUnitShort(selectedProductUnit) }}</InputGroupAddon>
          </InputGroup>
        </div>

        <div class="field">
          <label for="outbound-transaction-note-input">{{ translations.generic.note }}</label>
          <Textarea v-model:model-value="note" input-id="outbound-transaction-note-input"></Textarea>
        </div>

        <Message v-if="formError" severity="warn" variant="simple">{{ formError }}</Message>

        <div class="actions">
          <Button type="button" severity="secondary" :label="translations.generic.close" @click="hide"
            :disabled="loading"></Button>
          <Button icon="pi pi-save" :label="translations.generic.save" :loading="loading" :disabled="saveDisabled"
            type="submit"></Button>
        </div>
      </form>
      <aside v-if="hasSelectionDetails" class="selection-summary">
        <dl class="selection-details">
          <template v-if="selectedDelivery?.customer?.trim()">
            <dt>{{ translations.generic.customer }}</dt>
            <dd class="customer-name">{{ selectedDelivery.customer }}</dd>
          </template>
          <template v-if="selectedDelivery?.note?.trim()">
            <dt>{{ translations.outboundDelivery.transactionDialog.delivery }}: {{ translations.generic.note }}</dt>
            <dd>{{ selectedDelivery.note }}</dd>
          </template>
          <template v-if="selectedDeliveryRow?.note?.trim()">
            <dt>{{ translations.outboundDelivery.transactionDialog.deliveryRow }}: {{ translations.generic.note }}</dt>
            <dd>{{ selectedDeliveryRow.note }}</dd>
          </template>
        </dl>
      </aside>
    </div>
  </Dialog>
</template>

<style scoped>
.dialog-layout {
  display: grid;
  gap: 1.5rem;
}

.dialog-layout.has-details {
  grid-template-columns: minmax(0, 3fr) minmax(0, 2fr);
}

.selection-summary {
  min-width: 0;
}

.form-grid {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  min-width: 0;
}

.field {
  display: grid;
  gap: 0.25rem;
}

.selection-details {
  display: grid;
  gap: 0.25rem;
  margin: 0.25rem 0 0;
  font-size: 0.875rem;
}

.selection-details dt {
  font-weight: 600;
}

.selection-details dd {
  margin: 0;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
}

.customer-name {
  font-size: 1.125rem;
}

.actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  margin-top: 0.25rem;
}

@media (max-width: 640px) {
  .dialog-layout.has-details {
    grid-template-columns: minmax(0, 1fr);
  }
}
</style>
